<template>
    <div>
        <div id="spaceSetup">
            <div class="m-0 row">
                <div class="nav col-lg-4">
                    <div class="nav-content">
                        <div v-for="item in navItems" :key="item.key"
                             @click="setActiveItem(item)"
                             :class="['item cursor-pointer',
                             item.done ? 'done' : '',
                             item.key === activeItem ? 'active' : '' ]">
                            <div class="number">{{ item.order }}</div>
                            <p>{{ item.text }}</p>
                        </div>
                    </div>
                </div>
                <div class="content">
                    <template v-for="item in navItems">
                        <component
                            :key="item.key"
                            ref="stepComponentContent"
                            @stepDone="setItemDone(item.key)"
                            @prevStep="prevStep(item.key)"
                            @nextStep="nextStep(item.key)"
                            @finishSetup="finishSetup"
                            :step="item.order"
                            v-if="activeItem === item.key"
                            :is="item.component"
                            :done="item.done"
                            :allDone="allDone"
                            :simplified="true"
                            :props="item.componentProps"/>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import SetupClass from "./organisms/SetupClass.vue";
import Invite from "./organisms/Invite.vue";
import PlanMeal from "./organisms/PlanMeal.vue";
import SelectObservation from "./organisms/SelectObservation.vue";
import Modal from "../../theme/organisms/Modal.vue";
import Welcome from "./organisms/Welcome.vue";
import Finish from "./organisms/Finish.vue";

export default {
    name: "SpaceSetup",
    props: {
        space: {
            required: false
        },
        setup: {
            type: String | null,
            default: () => null
        },
        name: {
            type: String | null,
            default: null
        },
        selectedNav: {
            type: Object | null,
            default: null
        }
    },
    components: {
        SetupClass,
        Invite,
        PlanMeal,
        SelectObservation,
        Modal
    },
    computed: {
        lastStep() {
            return this.setupData ? (JSON.parse(this.setupData).length + 1 >= Object.keys(this.navItems).length) : false;
        }
    },
    watch: {
        activeItem() {
            if(!this.activeItemDone) {
                this.updateFooterText();
            }
        }
    },
    data() {
        return {
            setupData: null,
            language: document.documentElement.lang,
            activeItem: null,
            activeItemDone: false,
            navItems: {
                welcome: {
                    order: 0,
                    key: 'welcome',
                    text: trans('general.space_setup.welcome'),
                    done: this.space?.setup ?? false,
                    component: Welcome
                },
                add_first_class: {
                    order: 1,
                    key: 'add_first_class',
                    text: trans('general.space_setup.add_first_class'),
                    done: false,
                    component: SetupClass
                },
                invite_teachers: {
                    order: 2,
                    key: 'invite_teachers',
                    text: trans('general.space_setup.invite_teachers'),
                    done: false,
                    component: Invite,
                    componentProps: {
                        form: 'staff_enrollment',
                        showFooter: true,
                    }
                },
                register_parents_children: {
                    order: 3,
                    key: 'register_parents_children',
                    text: trans('general.space_setup.register_parents_children'),
                    done: false,
                    component: Invite,
                    componentProps: {
                        scope: 'parents',
                        form: 'child_enrollment',
                        showFooter: true,
                    }
                },
                plan_first_meal: {
                    order: 4,
                    key: 'plan_first_meal',
                    text: trans('general.space_setup.plan_first_meal'),
                    done: false,
                    component: PlanMeal,
                    componentProps: {
                        space: this.space,
                    }
                },
                // observation_methodology: {
                //     order: 5,
                //     key: 'observation_methodology',
                //     text: trans('general.space_setup.observation_methodology'),
                //     done: false,
                //     component: SelectObservation
                // },
                finish: {
                    order: 5,
                    key: 'finish',
                    text: trans('general.space_setup.finish'),
                    done: false,
                    component: Finish
                },
            },
            spaceData: JSON.parse(this.space),
            allDone: false
        }
    },
    methods: {
        initSpaceSetup() {
            axios.get('settings/read').then(response => {
                this.setupData = response.data.data.space.setup;

                if (this.setupData) {
                    JSON.parse(this.setupData).forEach(item => {
                        if (this.navItems[item]) {
                            this.navItems[item].done = true;
                        }
                    });
                }

                if (this.setupData && JSON.parse(this.setupData).includes('all')) {
                    this.setAllDone(false);
                } else {
                    if(this.selectedNav) {
                        this.setActiveItem(this.navItems[this.selectedNav]);
                    } else {
                        this.setNextItemActive();
                    }
                }
            });
        },
        setPrevItemActive(itemKey) {
            let itemsArray = Object.values(this.navItems);
            let currentIndex = itemsArray.findIndex(item => item.key === itemKey);

            let prevStep = itemsArray[currentIndex - 1];
            if (prevStep) {
                this.setActiveItem(prevStep);
            }
        },
        setNextItemActive(itemKey) {
            let itemsArray = Object.values(this.navItems);
            let currentIndex = itemsArray.findIndex(item => item.key === itemKey);

            let nextStep = itemsArray[currentIndex + 1];
            if (nextStep) {
                this.setActiveItem(nextStep);
                return;
            }

            if (itemsArray.every(item => item.done)) {
                this.setAllDone(true);
                return;
            }

            this.setAllDone(false);
        },
        setActiveItem(item) {
            this.activeItemDone = !!item.done;
            this.activeItem = item.key;
        },
        setItemDone(itemKey) {
            let setupItems = this.setupData ? JSON.parse(this.setupData) : [];

            if (!setupItems.includes(itemKey)) {
                setupItems.push(itemKey);

                let formData = new FormData();
                formData.append('setup', JSON.stringify(setupItems));

                axios.post('settings/update', formData)
                    .then(response => {
                        this.setupData = response.data.data.setup;
                        this.navItems[itemKey].done = true;
                        setupItems = this.setupData ? JSON.parse(this.setupData) : [];
                        const keys = Object.keys(this.navItems);
                        this.$emit('stepDone', {itemKey: itemKey});

                        const keysToCheck = keys.filter(key => key !== 'welcome' && key !== 'finish');
                        if (keysToCheck.every(key => setupItems.includes(key))) {
                            this.setAllDone();
                        } else {
                            if(itemKey.includes('invite_teachers', 'register_parents_children')) {
                                this.setNextItemActive(itemKey);
                            }
                        }
                    })
            }
        },
        prevStep(itemKey) {
            this.setPrevItemActive(itemKey);
        },
        nextStep(itemKey) {
            this.setNextItemActive(itemKey);
        },
        updateFooterText() {
            this.$nextTick(() => {
                if (this.$refs.stepComponentContent) {
                    const ref = Array.isArray(this.$refs.stepComponentContent) ? this.$refs.stepComponentContent[0] : this.$refs.stepComponentContent;
                    if (ref && typeof ref.setFooterBtnText === 'function') {
                        ref.setFooterBtnText(this.lastStep);
                    }
                }
            });
        },
        setAllDone(save = true) {
            if(save) {
                let formData = new FormData();
                formData.append('setup', JSON.stringify(["all"]));
                axios.post('settings/update', formData).then(response => {
                    this.allDone = true;
                });
            }

            Object.values(this.navItems).forEach(item => {
                item.done = true;
            });
        },
        finishSetup() {
            this.$emit('stepDone', {itemKey: this.allDone ? 'all' : 'closed'});
        }
    },
    mounted() {
        this.initSpaceSetup();
    }
}
</script>
